<template>
  <div class="flex flex-col px-4 md:px-0 pb-6 gap-3">
    <p class="text-xs text-gray-500 text-center leading-relaxed px-2 md:self-start">
      כל סניף הוא מיקום פיזי. עובדים יכולים לעבוד בכמה סניפים; ציוד משויך לסניף אחד בלבד.
    </p>

    <div class="flex flex-col gap-3 md:flex-row md:flex-wrap">
      <div
        v-for="branch in branches"
        :key="branch.id"
        class="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3.5 cursor-pointer active:bg-gray-50 md:max-w-64 w-full"
        @click="openBranch(branch)"
      >
        <div class="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-none">
          <i class="pi pi-map-marker text-lg" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-[15px] font-semibold truncate">{{ branch.name }}</div>
          <div v-if="branch.address" class="text-xs text-gray-500 truncate">{{ branch.address }}</div>
          <!-- Resource-count chip returns here when the equipment tab ships with the tan-booth feature. -->
          <div class="flex gap-1.5 mt-2">
          <span class="text-[11px] font-medium bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full flex items-center gap-1">
            <i class="pi pi-users text-[10px]" /> {{ employeeCount(branch.id) }} עובדים
          </span>
          </div>
        </div>
      </div>

      <button
        v-if="canManage"
        type="button"
        class="w-full md:max-w-64 flex items-center justify-center gap-2 py-3.5 rounded-xl border border-dashed border-gray-300 text-gray-500 text-[13px]"
        @click="openAddBranch"
      >
        <i class="pi pi-plus text-xs" /> הוספת סניף
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IBranch, MAIN_BRANCH_ID } from '@torim-app/types';
import { useDrawer } from '@torim-app/shared-components';
import { useAdminStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';
import AddBranch from '@app/components/admin/modals/AddBranch.vue';
import { teamMemberBranchIds } from './team-utils';
import { useAdminSettingsStore } from '@app/stores/admin-settings';

const adminStore = useAdminStore();
const settingsStore = useAdminSettingsStore();
const { openDrawer, closeDrawer } = useDrawer();

const canManage = computed(() => adminStore.getCanManageOthers);
const branches = computed(() => adminStore.getBranchSelectOptions);

const employeeCount = (branchId: string): number => {
  const clients = settingsStore.getSettings?.employees?.clients ?? [];
  return clients.filter((c) => {
    if (!c.userId) return false;
    const ids = teamMemberBranchIds(c.userId);
    if (!ids.length) return branchId === MAIN_BRANCH_ID;
    return ids.includes(branchId);
  }).length;
};

const openBranch = (branch: IBranch) => {
  const isMain = branch.id === MAIN_BRANCH_ID;
  trackEvent('resources_branch_clicked', { branch_id: branch.id });
  // The main branch is the business's own location — edit its raw title (without the
  // "(ראשי)" selector suffix) so saving doesn't append the suffix to the business name.
  const editBranch = isMain ? { ...branch, name: adminStore.getBusinessTitle } : branch;
  openDrawer(AddBranch, { branch: editBranch, onDone: closeDrawer, onClose: closeDrawer }, { title: isMain ? 'עריכת הסניף הראשי' : 'עריכת סניף' });
};

const openAddBranch = () => {
  trackEvent('team_add_branch_clicked');
  openDrawer(AddBranch, { onDone: closeDrawer, onClose: closeDrawer }, { title: 'הוספת סניף' });
};
</script>
